// #viet_code
import React, {useState} from 'react';
import {useHistory} from 'react-router-dom';
import Table from 'react-bootstrap/Table';
import {Badge} from 'react-bootstrap';
import {toast} from 'react-toastify';
import {useMutation, useQuery, useQueryClient} from 'react-query';
import {DeleteSingle, Get, Put} from '@app/utils/httpProvider';
import {MAINTENANCE_EVENT_API} from '@app/utils/apiUrl';
import {formatDateTimeMoment} from '../../../utils/helper';
import {messageToast} from '../../../utils/define';
import {ConfirmModal} from '../../../modules/modal/ConfirmModal';
import MaintenanceEventModal from './MaintenanceEventModal';
import ReactToPrint from 'react-to-print';
import MaintenanceDecisionPrintTemplate from './MaintenanceDecisionPrintTemplate';
import {useRef} from 'react';
import moment from 'moment';

const statusStyleMap = {
    'Đã phê duyệt': {
        bg: '#d1fae5',
        color: '#065f46',
        border: '#6ee7b7',
        icon: '✅'
    },
    'Đã sửa chữa': {
        bg: '#dbeafe',
        color: '#1e40af',
        border: '#93c5fd',
        icon: '🔧'
    },
    'Không phê duyệt': {
        bg: '#fee2e2',
        color: '#991b1b',
        border: '#fca5a5',
        icon: '❌'
    },
    'Chờ phê duyệt': {
        bg: '#fef9c3',
        color: '#854d0e',
        border: '#fde047',
        icon: '⏳'
    }
};

const StatusBadge = ({status}) => {
    const s = statusStyleMap[status];
    return (
        <span
            style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '4px',
                padding: '3px 10px',
                borderRadius: '999px',
                fontSize: '12px',
                fontWeight: 600,
                whiteSpace: 'nowrap',
                background: s ? s.bg : '#f3f4f6',
                color: s ? s.color : '#374151',
                border: `1px solid ${s ? s.border : '#d1d5db'}`
            }}
        >
            {s && <span>{s.icon}</span>}
            {status || '—'}
        </span>
    );
};

const PrintAction = ({item}) => {
    const componentRef = useRef();
    return (
        <>
            <div style={{display: 'none'}}>
                <MaintenanceDecisionPrintTemplate
                    ref={componentRef}
                    data={item}
                />
            </div>
            <ReactToPrint
                trigger={() => (
                    <button
                        type="button"
                        className="btn btn-xs btn-outline-secondary mr-1"
                        title="In Biên bản / Quyết định"
                    >
                        <i className="fas fa-print" />
                    </button>
                )}
                content={() => componentRef.current}
                pageStyle="@page { size: A4 portrait; margin: 15mm; } @media print { body { -webkit-print-color-adjust: exact; } }"
            />
        </>
    );
};

const MaintenanceEventTab = ({assetId, assetStatusId, assetStatuses}) => {
    const history = useHistory();
    const [showModal, setShowModal] = useState(false);
    const [editItem, setEditItem] = useState(null);
    const queryClient = useQueryClient();
    const [openConfirmDeleteModal, setOpenConfirmDeleteModal] = useState(false);
    const [idWantDelete, setIdWantDelete] = useState('');
    const [confirmDelMessage, setConfirmDelMessage] = useState('');

    const {isLoading, isError, error, data, refetch} = useQuery(
        ['maintenanceEventsByAsset', assetId],
        () =>
            Get(MAINTENANCE_EVENT_API.GET_PAGING, null, {
                AssetId: Number(assetId),
                PageIndex: 1,
                PageSize: 10000
            }),
        {enabled: !!assetId}
    );

    const deleteFunc = useMutation((value) =>
        DeleteSingle(MAINTENANCE_EVENT_API.DELETE_ITEM, value, null)
    );

    const approveFunc = useMutation((id) =>
        Put(`${MAINTENANCE_EVENT_API.APPROVE}/${id}`, null, {}, false)
    );

    const rejectFunc = useMutation((id) =>
        Put(`${MAINTENANCE_EVENT_API.REJECT}/${id}`, null, {}, false)
    );

    const completeFunc = useMutation((id) =>
        Put(`${MAINTENANCE_EVENT_API.COMPLETE}/${id}`, null, {}, false)
    );

    const handleAdd = () => {
        // Tìm trạng thái hiện tại dựa trên ID truyền vào
        // Sử dụng == để so sánh linh hoạt giữa string và number
        const currentStatus = assetStatuses?.find(s => s.id == assetStatusId);
        const name = currentStatus?.statusName?.toLowerCase() || '';
        const code = currentStatus?.statusCode?.toUpperCase() || '';
        
        // Chỉ cho phép tạo phiếu nếu là Tài sản mới (NH/MOI) hoặc Đang hoạt động (NH/ACTIVE)
        // Dựa trên bảng trạng thái bạn cung cấp: Green là ok, Yellow & Grey là block
        const isAllowed = name.includes('hoạt động') || name.includes('mới') || code === 'NH' || code === 'MOI' || code === 'ACTIVE';

        if (isAllowed) {
            history.push(`/phieu-sua-chua/them?assetId=${assetId}`);
        } else {
            toast.warning(`Tài sản đang ở trạng thái "${currentStatus?.statusName || 'không xác định'}", không thể tạo thêm phiếu sửa chữa mới.`);
        }
    };

    const handleEdit = (item) => {
        setEditItem(item);
        setShowModal(true);
    };

    const handleApprove = (id) => {
        approveFunc.mutate(id, {
            onSuccess: () => {
                queryClient.invalidateQueries(['maintenanceEventsByAsset', assetId]);
                queryClient.invalidateQueries([assetId]);
                toast.success('Phê duyệt thành công!');
            },
            onError: (err) => toast.error(err?.message || 'Phê duyệt thất bại!')
        });
    };

    const handleReject = (id) => {
        rejectFunc.mutate(id, {
            onSuccess: () => {
                queryClient.invalidateQueries(['maintenanceEventsByAsset', assetId]);
                queryClient.invalidateQueries([assetId]);
                toast.success('Đã từ chối phê duyệt!');
            },
            onError: (err) => toast.error(err?.message || 'Thao tác thất bại!')
        });
    };

    const handleComplete = (id) => {
        completeFunc.mutate(id, {
            onSuccess: () => {
                queryClient.invalidateQueries(['maintenanceEventsByAsset', assetId]);
                queryClient.invalidateQueries([assetId]);
                toast.success('Đã hoàn thành sửa chữa!');
            },
            onError: (err) => toast.error(err?.message || 'Thao tác thất bại!')
        });
    };

    const handleCloseModal = () => {
        setShowModal(false);
        setEditItem(null);
    };

    const handleSaveSuccess = () => {
        refetch();
        queryClient.invalidateQueries([assetId]);
        handleCloseModal();
    };

    const deleteAction = (id, name) => {
        setIdWantDelete(id);
        setConfirmDelMessage(name);
        setOpenConfirmDeleteModal(true);
    };

    const acceptDelete = async () => {
        if (idWantDelete !== '') {
            deleteFunc.mutate(idWantDelete, {
                onSuccess: () => {
                    queryClient.invalidateQueries(['maintenanceEventsByAsset', assetId]);
                    queryClient.invalidateQueries([assetId]);
                    toast.success(messageToast.messageToastDeleteSuccess);
                },
                onError: (err) => {
                    toast.error(err?.message || messageToast.messageToastDeleteError);
                }
            });
            setOpenConfirmDeleteModal(false);
        }
    };

    const cancelDelete = () => {
        setIdWantDelete('');
        setOpenConfirmDeleteModal(false);
    };

    const formatCurrency = (value) => {
        if (value == null) return '';
        return Number(value).toLocaleString('vi-VN') + ' đ';
    };

    const isOverdue = (date, status) => {
        if (!date || status === 'Đã sửa chữa') return false;
        return moment(date).isBefore(moment(), 'day');
    };

    const items = data?.data || [];

    return (
        <div className="maintenance-event-tab">
            <div className="d-flex justify-content-between align-items-center mb-3 mt-2">
                <h6 className="fw-bold mb-0 text-primary">
                    <i className="fas fa-history mr-2" />
                    Danh sách Phiếu sửa chữa & Bảo trì
                </h6>
                <div>
                    <button
                        type="button"
                        className="btn btn-info btn-sm"
                        onClick={handleAdd}
                    >
                        <i className="fas fa-plus mr-1" /> Thêm phiếu sửa chữa
                    </button>
                </div>
            </div>

            {isLoading ? (
                <div>Đang tải...</div>
            ) : isError ? (
                <div>Có lỗi: {error?.message}</div>
            ) : (
                <div style={{overflow: 'auto'}}>
                    <Table striped bordered hover size="sm">
                        <thead>
                            <tr>
                                <th>STT</th>
                                <th>Mã phiếu</th>
                                <th>Ngày SC</th>
                                <th>Hạn hoàn thành</th>
                                <th>Người làm</th>
                                <th>Lần SC</th>
                                <th>Chi phí</th>
                                <th>Trạng thái</th>
                                <th style={{width: '160px'}}>Thao tác</th>
                            </tr>
                        </thead>
                        {items.length > 0 ? (
                            <tbody>
                                {items.map((item, index) => {
                                    const overdue = isOverdue(item.endDate, item.status);
                                    return (
                                        <tr key={item.id} className={overdue ? 'table-danger' : ''}>
                                            <td>{index + 1}</td>
                                            <td>
                                                {item.eventCode}
                                                {overdue && (
                                                    <Badge bg="danger" className="ml-2" title="Quá hạn hoàn thành!">
                                                        <i className="fas fa-exclamation-triangle" />
                                                    </Badge>
                                                )}
                                            </td>
                                            <td> {item.eventDate ? formatDateTimeMoment(item.eventDate) : ''} </td>
                                            <td> {item.endDate ? formatDateTimeMoment(item.endDate) : '—'} </td>
                                            <td>{item.performedBy}</td>
                                            <td className="text-center">{item.repairCount || '—'}</td>
                                            <td>{formatCurrency(item.cost)}</td>
                                            <td> <StatusBadge status={item.status} /> </td>
                                            <td>
                                                <div className="d-flex justify-content-center">
                                                    {item.maintenanceBatchId && (
                                                        <button
                                                            type="button"
                                                            className="btn btn-xs btn-outline-info"
                                                            onClick={() => history.push(`/phieu-sua-chua/chi-tiet/${item.maintenanceBatchId}`)}
                                                            title="Xem chi tiết phiếu sửa chữa"
                                                            style={{
                                                                width: '32px', 
                                                                height: '32px', 
                                                                padding: '0', 
                                                                display: 'inline-flex', 
                                                                alignItems: 'center', 
                                                                justifyContent: 'center',
                                                                borderRadius: '50%',
                                                                transition: 'all 0.2s'
                                                            }}
                                                        >
                                                            <i className="fas fa-eye" style={{fontSize: '14px'}} />
                                                        </button>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        ) : (
                            <tbody>
                                <tr>
                                    <td colSpan="9" className="text-center">Chưa có phiếu sửa chữa nào</td>
                                </tr>
                            </tbody>
                        )}
                    </Table>
                </div>
            )}

            <MaintenanceEventModal
                show={showModal}
                onHide={handleCloseModal}
                onSuccess={handleSaveSuccess}
                assetId={assetId}
                editItem={editItem}
            />

            <ConfirmModal
                isOpen={openConfirmDeleteModal}
                confirmType="deleteSubItem"
                handleAccept={() => acceptDelete()}
                handleCancel={() => cancelDelete()}
                message={`Bạn chắc chắn xóa phiếu sửa chữa: ${confirmDelMessage} ?`}
            />
        </div>
    );
};

export default MaintenanceEventTab;
